import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import { USERS_API_URL } from '../../config/apiConfig';
import { WikiBadge, IndustryVIPBadge } from '../ContributorsScreen/ContributorsScreen';
import { DiscussionBadge } from '../ContributorsScreen/ContributorsScreen';
import ArtifactViewer from '../ArtifactViewer/ArtifactViewer';
import ShareButton from '../ShareButton/ShareButton';
import UpgradeModal from '../UpgradeModal/UpgradeModal';
import UploadOverlay from '../UploadOverlay/UploadOverlay';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { linkifyHtml } from '../../utils/linkify';
import SetHomepageButton from '../SetHomepageButton/SetHomepageButton';
import ReactionButton from '../shared/ReactionButton';
import '../../styles/ampDescriptionDisplay.css';
import './DiscussionsScreen.css';

// Character limit constants
const CHAR_SOFT_LIMIT  = 10000;  // indicator turns red
const CHAR_WARNING     = 9000;   // indicator turns yellow
const CHAR_HARD_LIMIT  = 10500;  // submit disabled (grace for finishing a sentence)
const CHAR_COLLAPSE    = 2000;   // show first N chars of long messages, then expand

// Strip HTML tags to get plain text (for character counting)
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = safeStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Styles
const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
    fontSize: '14px'
  },
  breadcrumbLink: {
    color: '#2563eb',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  breadcrumbSeparator: {
    color: '#666'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },
  cardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
  },
  categoryCard: {
    borderLeft: '4px solid #2563eb'
  },
  groupCard: {
    borderLeft: '4px solid #48bb78'
  },
  topicCard: {
    borderLeft: '4px solid #ed8936'
  },
  messageCard: {
    borderLeft: '4px solid #e2e8f0',
    cursor: 'default'
  },
  replyCard: {
    borderLeft: '4px solid #cbd5e0',
    cursor: 'default'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '8px'
  },
  categoryTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '8px'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#718096',
    marginBottom: '12px'
  },
  cardMeta: {
    fontSize: '12px',
    color: '#a0aec0',
    display: 'flex',
    gap: '16px'
  },
  button: {
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  buttonSecondary: {
    background: '#e2e8f0',
    color: '#4a5568'
  },
  buttonDanger: {
    background: '#fc8181',
    color: 'white'
  },
  form: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    minHeight: '100px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px'
  },
  userPhoto: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #e2e8f0'
  },
  userPhotoPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '16px'
  },
  userName: {
    fontWeight: '600',
    color: '#2d3748'
  },
  messageContent: {
    fontSize: '15px',
    color: '#4a5568',
    lineHeight: '1.6',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  },
  flagButton: {
    background: 'none',
    border: 'none',
    color: '#a0aec0',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'color 0.2s, background 0.2s'
  },
  flagged: {
    color: '#fc8181',
    background: '#fff5f5'
  },
  hidden: {
    opacity: 0.5,
    background: '#f7fafc'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#718096'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginTop: '16px'
  }
};

// Character Counter Component
const CharacterCounter = ({ length }) => {
  const color = length >= CHAR_SOFT_LIMIT ? '#e53e3e'
    : length >= CHAR_WARNING ? '#d69e2e'
    : '#48bb78';
  const overLimit = length > CHAR_HARD_LIMIT;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '8px',
      marginTop: '6px',
      fontSize: '12px',
      color
    }}>
      {overLimit && (
        <span style={{ color: '#e53e3e', fontWeight: '600' }}>
          Please shorten your message to submit
        </span>
      )}
      <span style={{ fontWeight: '600' }}>
        {length.toLocaleString()} / {CHAR_SOFT_LIMIT.toLocaleString()}
      </span>
    </div>
  );
};

// Collapsible Message Content Component
const COLLAPSE_HEIGHT = 300; // px — collapsed preview height

const CollapsibleMessage = ({ content, style }) => {
  const [expanded, setExpanded] = useState(false);
  const plainText = stripHtml(content);
  const needsCollapse = plainText && plainText.length > CHAR_COLLAPSE;

  if (!needsCollapse) {
    return <div className="content-editor" style={style} dangerouslySetInnerHTML={{ __html: linkifyHtml(content) }} />;
  }

  return (
    <div style={style}>
      <div style={{ position: 'relative' }}>
        <div
          className="content-editor"
          style={expanded ? undefined : { maxHeight: `${COLLAPSE_HEIGHT}px`, overflow: 'hidden' }}
          dangerouslySetInnerHTML={{ __html: linkifyHtml(content) }}
        />
        {!expanded && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60px',
            background: 'linear-gradient(to bottom, transparent, white)',
            pointerEvents: 'none'
          }} />
        )}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'block',
          marginTop: '8px',
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '600',
          padding: 0
        }}
      >
        {expanded ? '\u25B2 Show less' : '\u25BC Show more'}
      </button>
    </div>
  );
};

// Copy Link Button — copies a URL to clipboard, shows "Copied!" briefly
const CopyLinkButton = ({ url, label = '🔗 Copy link' }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = fullUrl;
      ta.style.position = 'fixed';
      ta.style.left = '-999999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      title="Copy link"
      style={{
        background: 'none',
        border: 'none',
        color: copied ? '#28a745' : '#a0aec0',
        cursor: 'pointer',
        fontSize: '12px',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'color 0.2s',
        fontWeight: copied ? '600' : 'normal'
      }}
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
};

// User Photo Component
const UserPhoto = ({ userId, displayName, hasPhoto, size = 40 }) => {
  const photoStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #e2e8f0'
  };
  
  const placeholderStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    fontSize: `${Math.max(size * 0.4, 10)}px`
  };

  if (hasPhoto && userId) {
    return (
      <img 
        src={`${USERS_API_URL}/users/${userId}/photo`}
        alt={displayName || 'User'}
        style={photoStyle}
        onError={(e) => {
          e.target.style.display = 'none';
          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
        }}
      />
    );
  }
  return (
    <div style={placeholderStyle}>
      {(displayName || 'U').charAt(0).toUpperCase()}
    </div>
  );
};

// Group Image/Icon component
const GroupImage = ({ groupId, groupName, hasImage, size = 50 }) => {
  const imageStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0
  };

  const placeholderStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '8px',
    background: '#28a745',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: `${Math.max(size * 0.5, 16)}px`,
    flexShrink: 0
  };

  if (hasImage && groupId) {
    return (
      <img 
        src={`${API_URL}/discussion-groups/${groupId}/image`}
        alt={groupName || 'Group'}
        style={imageStyle}
        onError={(e) => {
          // If image fails to load, replace with placeholder
          e.target.style.display = 'none';
          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
        }}
      />
    );
  }
  return (
    <div style={placeholderStyle}>
      💬
    </div>
  );
};

// View Components
const CategoryList = ({ categories, groups, onSelectCategory, onSelectGroup }) => {
  const [hoveredId, setHoveredId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Auto-expand all categories on first render
  useEffect(() => {
    const expanded = {};
    categories.forEach(cat => {
      expanded[cat.category_id] = true;
    });
    setExpandedCategories(expanded);
  }, [categories]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  if (categories.length === 0) {
    return (
      <div style={styles.emptyState}>
        <h3>No Discussion Categories</h3>
        <p>There are no discussion categories available yet.</p>
      </div>
    );
  }

  return (
    <div>
      {categories.map(cat => {
        const categoryGroups = groups.filter(g => g.category_id === cat.category_id);
        const isExpanded = expandedCategories[cat.category_id];
        
        return (
          <div key={cat.category_id} style={{ marginBottom: '16px' }}>
            <div
              style={{
                ...styles.card,
                ...styles.categoryCard,
                marginBottom: isExpanded ? '0' : '16px',
                borderRadius: isExpanded ? '12px 12px 0 0' : '12px',
                ...(hoveredId === `cat-${cat.category_id}` ? styles.cardHover : {})
              }}
              onClick={() => toggleCategory(cat.category_id)}
              onMouseEnter={() => setHoveredId(`cat-${cat.category_id}`)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={styles.categoryTitle}>
                    <span style={{ marginRight: '8px' }}>{isExpanded ? '▼' : '▶'}</span>
                    {cat.category_name}
                  </div>
                  {cat.category_description && (
                    <div style={styles.cardDescription}>{cat.category_description}</div>
                  )}
                </div>
                <div style={styles.cardMeta}>
                  <span>📁 {categoryGroups.length} groups</span>
                </div>
              </div>
            </div>
            
            {isExpanded && (
              <div style={{
                background: '#f7fafc',
                borderLeft: '4px solid #2563eb',
                borderRight: '1px solid #e2e8f0',
                borderBottom: '1px solid #e2e8f0',
                borderRadius: '0 0 12px 12px',
                padding: '12px'
              }}>
                {categoryGroups.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#718096', fontSize: '14px' }}>
                    No discussion groups in this category yet.
                  </div>
                ) : (
                  categoryGroups.map(group => (
                    <div
                      key={group.group_id}
                      className="dsc-group-card-flex"
                      style={{
                        ...styles.card,
                        ...styles.groupCard,
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        ...(hoveredId === `grp-${group.group_id}` ? styles.cardHover : {})
                      }}
                      onClick={(e) => { e.stopPropagation(); onSelectGroup(group); }}
                      onMouseEnter={() => setHoveredId(`grp-${group.group_id}`)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      {/* Group Icon/Image */}
                      <GroupImage 
                        groupId={group.group_id}
                        groupName={group.group_name}
                        hasImage={group.has_image}
                        size={50}
                      />
                      
                      {/* Group Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={styles.cardTitle}>{group.group_name}</div>
                        {group.group_description && (
                          <div style={styles.cardDescription}>{group.group_description}</div>
                        )}
                      </div>
                      
                      {/* Stats */}
                      <div className="dsc-group-stats" style={{ 
                        display: 'flex', 
                        gap: '24px', 
                        alignItems: 'center',
                        flexShrink: 0,
                        fontSize: '13px'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: '700', fontSize: '18px', color: '#2d3748' }}>
                            {group.topic_count || 0}
                          </div>
                          <div style={{ color: '#718096', fontSize: '11px' }}>Topics</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: '700', fontSize: '18px', color: '#2d3748' }}>
                            {group.message_count || 0}
                          </div>
                          <div style={{ color: '#718096', fontSize: '11px' }}>Messages</div>
                        </div>
                        
                        {/* Last Post Info */}
                        {group.last_topic_title && (
                          <div className="dsc-last-post" style={{ 
                            borderLeft: '1px solid #e2e8f0', 
                            paddingLeft: '16px',
                            minWidth: '200px',
                            maxWidth: '300px'
                          }}>
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#718096',
                              marginBottom: '4px'
                            }}>
                              Last Post
                            </div>
                            <div style={{ 
                              fontSize: '13px', 
                              color: '#4a5568',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              marginBottom: '4px'
                            }}>
                              {group.last_topic_title}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <UserPhoto 
                                userId={group.last_post_user_id} 
                                displayName={group.last_post_user_name}
                                hasPhoto={group.last_post_user_has_photo}
                                size={20}
                              />
                              <span style={{ fontSize: '12px', color: '#718096' }}>
                                {group.last_post_user_name}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── VideoTopicCard ────────────────────────────────────────────────────────────
// Shown when a discussion topic has a youtube_url set.

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

const VideoTopicCard = ({ topic }) => {
  const videoId = extractYouTubeId(topic.youtube_url);

  if (!videoId) return null;

  const thumbnail  = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const youtubeUrl = `https://youtu.be/${videoId}`;

  return (
    <div
      style={{ border: '1px solid #e9ecef', borderRadius: '10px', overflow: 'hidden', background: '#fff', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      onClick={e => e.stopPropagation()}
    >
      {/* Thumbnail — links to YouTube (app or browser) */}
      <a
        href={youtubeUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'block', position: 'relative', width: '100%', maxWidth: '480px', margin: '0 auto' }}
        title="Watch on YouTube"
      >
        <img
          src={thumbnail}
          alt={topic.youtube_title || 'YouTube video'}
          style={{ width: '100%', display: 'block', borderRadius: '10px 10px 0 0' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
        {/* Play button overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.18)',
          borderRadius: '10px 10px 0 0',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(255,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}>
            <span style={{ color: '#fff', fontSize: '20px', marginLeft: '4px', lineHeight: 1 }}>▶</span>
          </div>
        </div>
      </a>

      {/* Title & channel */}
      <div style={{ padding: '0.75rem 1rem' }}>
        {topic.youtube_channel && (
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', marginBottom: '3px' }}>
            {topic.youtube_channel}
          </div>
        )}
        {topic.youtube_title && (
          <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1a2e', lineHeight: 1.3 }}>
            {topic.youtube_title}
          </div>
        )}
        <a
          href={youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-block', marginTop: '8px', color: '#2563eb', fontSize: '0.82rem', textDecoration: 'none' }}
        >
          ▶ Watch on YouTube
        </a>
      </div>
    </div>
  );
};

const AmpTopicCard = ({ topic }) => {
  const pinnedId = topic.amplifier_pinned_artifact_id || topic.amplifier_first_photo_id || null;
  const [photos, setPhotos] = useState(pinnedId ? [{ artifact_id: pinnedId, mime_type: 'image/jpeg' }] : []);
  const [selectedId, setSelectedId] = useState(pinnedId);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const mfr = topic.amplifier_manufacturer || '';
  const model = topic.amplifier_model || topic.amplifier_name || '';
  const yearStr = topic.amplifier_start_year
    ? (topic.amplifier_end_year && topic.amplifier_end_year !== topic.amplifier_start_year
        ? `${topic.amplifier_start_year}\u2013${topic.amplifier_end_year}`
        : String(topic.amplifier_start_year))
    : '';

  // Strip __RAW_HTML__ prefix and extract body content from full HTML documents
  const descriptionHtml = (() => {
    let d = topic.amplifier_description;
    if (!d) return null;
    if (d.startsWith('__RAW_HTML__')) d = d.slice('__RAW_HTML__'.length);
    const bodyMatch = d.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return bodyMatch ? bodyMatch[1] : d;
  })();
  const ampUrl = topic.manufacturer_slug && topic.amplifier_slug
    ? `/amps/${topic.manufacturer_slug}/${topic.amplifier_slug}`
    : `/amplifier-components/${topic.amplifier_id}`;

  useEffect(() => {
    if (!topic.amplifier_id) return;
    api.get(`/amplifier_artifacts?amplifier_id=${topic.amplifier_id}&artifact_type=photo`)
      .then(res => {
        if (res.data && res.data.length > 0) {
          // Put pinned photo first
          const sorted = pinnedId
            ? [
                ...res.data.filter(p => p.artifact_id === pinnedId),
                ...res.data.filter(p => p.artifact_id !== pinnedId),
              ]
            : res.data;
          setPhotos(sorted);
          setSelectedId(sorted[0].artifact_id);
        }
      })
      .catch(() => {});
  }, [topic.amplifier_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openViewerForArtifact = useCallback((artifactId) => {
    const idx = photos.findIndex((p) => p.artifact_id === artifactId);
    if (idx >= 0) {
      setViewerIndex(idx);
      setViewerOpen(true);
    }
  }, [photos]);

  const closeViewer = useCallback(() => setViewerOpen(false), []);
  const prevViewer = useCallback(() => setViewerIndex(i => Math.max(i - 1, 0)), []);
  const nextViewer = useCallback(() => setViewerIndex(i => Math.min(i + 1, photos.length - 1)), [photos.length]);

  return (
    <div
      style={{
        border: '1px solid #e9ecef',
        borderRadius: '10px',
        overflow: 'hidden',
        background: '#fff',
        marginBottom: '16px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Photo gallery: pinned image left, thumbnail grid right */}
      {photos.length > 0 && (
        <div style={{ display: 'flex', height: '240px', borderBottom: '1px solid #f0f0f0' }}>
          {/* Pinned / selected photo — fixed width, left-justified */}
          <div style={{ flexShrink: 0, width: '200px', height: '100%', overflow: 'hidden', background: '#f8f9fa', borderRight: '1px solid #e9ecef', cursor: 'zoom-in' }}
            onClick={() => openViewerForArtifact(selectedId)}
          >
            <img
              src={`${API_URL}/amplifier_artifacts/${selectedId}/thumbnail`}
              alt={model}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
            />
          </div>
          {/* Thumbnail grid — fills remaining width, fixed 68px squares, scrolls vertically */}
          {photos.length > 1 && (
            <div style={{
              flex: 1,
              height: '100%',
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 68px)',
              gridAutoRows: '68px',
              gap: '4px',
              padding: '6px',
              background: '#fafafa',
              alignContent: 'start',
              justifyContent: 'start',
              boxSizing: 'border-box',
            }}>
              {photos.map(p => (
                <button
                  key={p.artifact_id}
                  onClick={() => { setSelectedId(p.artifact_id); openViewerForArtifact(p.artifact_id); }}
                  style={{
                    border: p.artifact_id === selectedId ? '2px solid #2563eb' : '2px solid transparent',
                    borderRadius: '4px',
                    padding: 0,
                    cursor: 'zoom-in',
                    background: 'none',
                    overflow: 'hidden',
                    width: '68px',
                    height: '68px',
                  }}
                >
                  <img
                    src={`${API_URL}/amplifier_artifacts/${p.artifact_id}/thumbnail`}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Amp info */}
      <div style={{ padding: '0.9rem 1.1rem 1rem' }}>
        {mfr && (
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', marginBottom: '4px' }}>
            {mfr}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}>
          <a
            href={ampUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 600, fontSize: '1rem', color: '#2563eb', textDecoration: 'none' }}
            onMouseEnter={e => e.target.style.textDecoration = 'underline'}
            onMouseLeave={e => e.target.style.textDecoration = 'none'}
          >
            {model}
          </a>
          {yearStr && (
            <span style={{ fontSize: '0.75rem', color: '#999' }}>{yearStr}</span>
          )}
        </div>
        {descriptionHtml && (
          <div
            className="content-editor"
            style={{ fontSize: '13px', color: '#555', maxHeight: expanded ? 'none' : '80px', overflow: 'hidden', position: 'relative' }}
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        )}
        {descriptionHtml && (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}
            style={{ display: 'inline-block', marginTop: '8px', fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            {expanded ? '▲ Collapse' : '▼ Show full description'}
          </button>
        )}
      </div>

      {viewerOpen && photos.length > 0 && (
        <ArtifactViewer
          artifacts={photos}
          currentIndex={viewerIndex}
          onClose={closeViewer}
          onPrev={prevViewer}
          onNext={nextViewer}
          amplifierId={topic.amplifier_id}
          ampUrl={ampUrl}
        />
      )}
    </div>
  );
};



const MessageList = ({ messages, topicId, onReply, onFlag, onEdit = null, currentUserId, isAdmin, canInteract = true, replyTo, onSubmitReply, onCancelReply, compactMode = false, watchedMessageIds = new Set(), watchTogglingMsgIds = new Set(), onToggleMessageWatch = () => {}, messageReactions = {}, userMessageReactions = {}, onReactToMessage = null }) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [viewerArtifact, setViewerArtifact] = useState(null);

  // Click-delegation handler for any <img> embedded in rich-text content.
  // Handles both data-artifact-id thumbnails and plain base64/URL images from paste.
  // Images inside <a href> (e.g. amp-embed-card thumbnails) are skipped so the
  // link can handle the click and navigate to the amp page as expected.
  const handleContentImageClick = (e) => {
    const img = e.target.closest('img');
    if (!img) return;
    // Let links handle their own clicks (amp cards, etc.)
    if (img.closest('a[href]')) return;
    e.stopPropagation();
    const artifactId = img.getAttribute('data-artifact-id');
    if (artifactId) {
      setViewerArtifact({ artifact_id: artifactId, mime_type: 'image/jpeg', filename: img.alt || 'Image' });
    } else if (img.src) {
      // Inline base64 or external URL — derive mime type from data URI prefix if available
      const mimeType = img.src.startsWith('data:')
        ? img.src.split(';')[0].slice(5) || 'image/jpeg'
        : 'image/jpeg';
      setViewerArtifact({ _url: img.src, mime_type: mimeType, filename: img.alt || 'Image' });
    }
  };

  const toggleExpanded = (messageId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  // Build a tree structure for threaded messages
  const buildMessageTree = (msgs) => {
    const messageMap = {};
    const roots = [];
    
    // First pass: create map
    msgs.forEach(msg => {
      messageMap[msg.message_id] = { ...msg, replies: [] };
    });
    
    // Second pass: build tree
    msgs.forEach(msg => {
      if (msg.parent_message_id && messageMap[msg.parent_message_id]) {
        messageMap[msg.parent_message_id].replies.push(messageMap[msg.message_id]);
      } else {
        roots.push(messageMap[msg.message_id]);
      }
    });
    
    return roots;
  };

  const renderMessage = (message, depth = 0) => {
    const isHidden = message.is_hidden;
    const isFlagged = message.is_flagged;
    const isReply = depth > 0;
    const indentPx = depth * (compactMode ? 20 : 40);
    const isBeingRepliedTo = replyTo?.message_id === message.message_id;

    // ── compact mode ──
    if (compactMode && !expandedIds.has(message.message_id)) {
      const preview = stripHtml(message.message_content || '').replace(/\n/g, ' ');
      return (
        <div key={message.message_id} id={`message-${message.message_id}`}>
          <div
            onClick={() => toggleExpanded(message.message_id)}
            className="dsc-msg-indent"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              marginLeft: `${indentPx}px`,
              borderBottom: '1px solid #f0f0f0',
              background: isHidden ? '#f7fafc' : 'white',
              opacity: isHidden ? 0.55 : 1,
              cursor: 'pointer',
              minHeight: '32px',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
            onMouseLeave={(e) => e.currentTarget.style.background = isHidden ? '#f7fafc' : 'white'}
          >
            <UserPhoto userId={message.user_id} displayName={message.display_name} hasPhoto={message.has_photo} size={22} />
            <span style={{ fontWeight: '600', fontSize: '13px', color: '#2d3748', whiteSpace: 'nowrap' }}>
              {message.display_name || 'Anonymous'}
            </span>
            {message.has_image && <span title="Has image" style={{ fontSize: '12px', flexShrink: 0 }}>📷</span>}
            <span style={{
              fontSize: '13px',
              color: '#718096',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0
            }}>
              {preview}
            </span>
            <span style={{ fontSize: '11px', color: '#a0aec0', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {new Date(message.created_at).toLocaleDateString()}
            </span>
          </div>
          {message.replies && message.replies.length > 0 && (
            <div>{message.replies.map(r => renderMessage(r, depth + 1))}</div>
          )}
        </div>
      );
    }

    // ── full / expanded row ──
    const isInlineExpanded = compactMode && expandedIds.has(message.message_id);
    return (
      <div key={message.message_id} id={`message-${message.message_id}`}>
        {isInlineExpanded && (
          <div
            onClick={() => toggleExpanded(message.message_id)}
            className="dsc-msg-indent"
            style={{
              marginLeft: `${indentPx}px`,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: '11px',
              color: '#2563eb',
              fontWeight: '600',
              userSelect: 'none'
            }}
          >
            ▲ Collapse
          </div>
        )}
        <div
          className="dsc-msg-indent"
          style={{
            ...styles.card,
            ...(isReply ? styles.replyCard : styles.messageCard),
            marginLeft: `${indentPx}px`,
            ...(isHidden ? styles.hidden : {}),
            ...(isBeingRepliedTo ? { borderColor: '#4299e1', borderWidth: '2px' } : {})
          }}
        >
          {isHidden && (
            <div style={{ color: '#fc8181', fontSize: '12px', marginBottom: '8px' }}>
              ⚠️ This message has been hidden by moderators
            </div>
          )}
          <div style={styles.userInfo}>
            <UserPhoto 
              userId={message.user_id}
              displayName={message.display_name}
              hasPhoto={message.has_photo}
            />
            <div style={{ display: 'none' }}>
              <div style={styles.userPhotoPlaceholder}>
                {(message.display_name || 'U').charAt(0).toUpperCase()}
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={styles.userName}>{message.display_name || 'Anonymous'}</span>
                {message.is_industry_vip && <IndustryVIPBadge />}
                {message.is_verified_owner && (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap' }}>Verified Owner</span>
                )}
                {!message.is_verified_owner && parseInt(message.owned_count) >= 10 && (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap' }}>Amp Collector</span>
                )}
                {!message.is_verified_owner && parseInt(message.owned_count) >= 1 && parseInt(message.owned_count) < 10 && (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap' }}>Member</span>
                )}
                {parseInt(message.wiki_trust_level) >= 1 && (
                  <WikiBadge trustLevel={parseInt(message.wiki_trust_level)} />
                )}
                {parseInt(message.discussion_level) >= 1 && (
                  <DiscussionBadge discussionLevel={parseInt(message.discussion_level)} />
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                {new Date(message.created_at).toLocaleString()}
                {message.is_edited && <span style={{ marginLeft: '6px', fontStyle: 'italic' }}>(edited)</span>}
              </div>
            </div>
          </div>
          {editingMessageId === message.message_id ? (
            <div style={{ marginTop: '6px' }}>
              <RichTextEditor
                value={editingContent}
                onChange={setEditingContent}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  style={{ ...styles.flagButton, background: '#2563eb', color: 'white', fontWeight: '600' }}
                  onClick={() => {
                    if (onEdit) onEdit(message.message_id, editingContent);
                    setEditingMessageId(null);
                  }}
                >
                  Save
                </button>
                <button
                  style={styles.flagButton}
                  onClick={() => setEditingMessageId(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div onClick={handleContentImageClick}>
              <CollapsibleMessage content={message.message_content} style={styles.messageContent} />
            </div>
          )}
          {message.has_image && (
            <div style={{ marginTop: '12px' }}>
              <img 
                src={`${API_URL}/discussion-messages/${message.message_id}/image`}
                alt="Message attachment"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '8px',
                  cursor: 'zoom-in',
                  objectFit: 'contain',
                  border: '1px solid #4a5568'
                }}
                onClick={() => setViewerArtifact({
                  _url: `${API_URL}/discussion-messages/${message.message_id}/image`,
                  mime_type: message.image_mime_type || 'image/jpeg',
                  filename: 'Message attachment'
                })}
              />
            </div>
          )}
          {/* Reaction buttons — visible to all, clickable only when logged in */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '10px', alignItems: 'center' }}>
            {['up', 'down'].map(type => (
              <ReactionButton
                key={type}
                type={type}
                count={(messageReactions[message.message_id] || { up: 0, down: 0 })[type] || 0}
                active={userMessageReactions[message.message_id] === type}
                onClick={() => onReactToMessage && onReactToMessage(message.message_id, type)}
                disabled={!onReactToMessage}
                entityType="message"
                entityId={message.message_id}
                minWidth="48px"
              />
            ))}
            {topicId && (
              <CopyLinkButton url={`/discussions/topic/${topicId}#message-${message.message_id}`} />
            )}
          </div>
          {canInteract && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button 
                style={styles.flagButton}
                onClick={() => onReply(message)}
              >
                ↩️ Reply
              </button>
              {message.user_id === currentUserId && onEdit && (
                <button
                  style={styles.flagButton}
                  onClick={() => {
                    setEditingMessageId(message.message_id);
                    setEditingContent(message.message_content);
                  }}
                >
                  ✏️ Edit
                </button>
              )}
              {message.user_id !== currentUserId && (
                <button 
                  style={{
                    ...styles.flagButton,
                    ...(isFlagged ? styles.flagged : {})
                  }}
                  onClick={() => onFlag(message.message_id)}
                >
                  🚩 {isFlagged ? 'Flagged' : 'Flag'}
                </button>
              )}
              <button
                style={{
                  ...styles.flagButton,
                  background: watchedMessageIds.has(message.message_id) ? '#c6f6d5' : undefined,
                  color: watchedMessageIds.has(message.message_id) ? '#276749' : undefined,
                  opacity: watchTogglingMsgIds.has(message.message_id) ? 0.6 : 1
                }}
                onClick={() => onToggleMessageWatch(message.message_id)}
                title={watchedMessageIds.has(message.message_id)
                  ? 'Stop watching — you won\'t be notified of replies to this message'
                  : 'Watch this message — get notified when someone replies to it'}
                disabled={watchTogglingMsgIds.has(message.message_id)}
              >
                {watchedMessageIds.has(message.message_id) ? '👁 Watching' : '👁 Watch'}
              </button>
            </div>
          )}
        </div>
        
        {/* Inline reply form - appears below the message being replied to, indented */}
        {isBeingRepliedTo && onSubmitReply && (
          <div className="dsc-msg-indent" style={{ marginLeft: `${(depth + 1) * 40}px`, marginTop: '8px', marginBottom: '8px' }}>
            <MessageForm 
              onSubmit={onSubmitReply}
              onCancel={onCancelReply}
              replyTo={replyTo}
            />
          </div>
        )}
        
        {message.replies && message.replies.length > 0 && (
          <div>
            {message.replies.map(reply => renderMessage(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const messageTree = buildMessageTree(messages);
  
  if (messageTree.length === 0) {
    return (
      <div style={{ ...styles.emptyState, padding: '16px 20px' }}>
        <p style={{ margin: 0 }}>{canInteract ? 'Be the first to post a message!' : 'No messages yet. Log in to join the discussion.'}</p>
      </div>
    );
  }

  return (
    <div>
      {messageTree.map(msg => renderMessage(msg))}
      {viewerArtifact && (
        <ArtifactViewer
          artifacts={[viewerArtifact]}
          currentIndex={0}
          onClose={() => setViewerArtifact(null)}
          onPrev={() => {}}
          onNext={() => {}}
        />
      )}
    </div>
  );
};

// Form Components
const TopicForm = ({ onSubmit, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');

  const textLength = stripHtml(content).length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Topic title is required');
      return;
    }
    if (textLength > CHAR_HARD_LIMIT) {
      setError(`Message is too long (${textLength.toLocaleString()} characters). Please shorten it to around ${CHAR_SOFT_LIMIT.toLocaleString()} characters.`);
      return;
    }
    onSubmit({ title, initial_message: content });
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <h3 style={{ marginBottom: '20px' }}>Create New Topic</h3>
      {error && <div style={{ color: '#fc8181', marginBottom: '12px' }}>{error}</div>}
      <div style={styles.formGroup}>
        <label style={styles.label}>Topic Title *</label>
        <input
          style={styles.input}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter topic title"
        />
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Initial Message</label>
        <RichTextEditor
          value={content}
          onChange={setContent}
          placeholder="Start the discussion..."
        />
        <CharacterCounter length={textLength} />
      </div>
      <div style={styles.buttonGroup}>
        <button type="submit" style={styles.button} disabled={textLength > CHAR_HARD_LIMIT}>Create Topic</button>
        <button 
          type="button" 
          style={{ ...styles.button, ...styles.buttonSecondary }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// ── VideoTopicForm — shown instead of TopicForm in the Amp Videos group ───────
const VideoTopicForm = ({ onSuccess, onCancel }) => {
  const [url, setUrl] = useState('');
  const [titleOverride, setTitleOverride] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handlePreview = async () => {
    if (!url.trim()) return;
    setPreviewLoading(true);
    setPreview(null);
    setPreviewError(null);
    try {
      const res = await api.get(`/amp-videos/preview?url=${encodeURIComponent(url.trim())}`);
      setPreview(res.data);
      if (!titleOverride && res.data.title) setTitleOverride(res.data.title);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Could not load video info — check the URL');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post('/amp-videos/post-now', {
        youtube_url: url.trim(),
        title: titleOverride.trim() || undefined,
      });
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post video');
      setSubmitting(false);
    }
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <h3 style={{ marginBottom: '20px' }}>Add a Video</h3>
      {error && <div style={{ color: '#fc8181', marginBottom: '12px' }}>{error}</div>}
      <div style={styles.formGroup}>
        <label style={styles.label}>YouTube URL</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...styles.input, flex: 1 }}
            type="url"
            value={url}
            onChange={e => { setUrl(e.target.value); setPreview(null); setPreviewError(null); }}
            placeholder="https://www.youtube.com/watch?v=..."
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePreview(); } }}
          />
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 16px', whiteSpace: 'nowrap' }}
            onClick={handlePreview}
            disabled={!url.trim() || previewLoading}
          >
            {previewLoading ? 'Loading…' : 'Preview'}
          </button>
        </div>
        {previewError && <div style={{ color: '#fc8181', marginTop: '6px', fontSize: '14px' }}>{previewError}</div>}
      </div>
      {preview && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', background: '#f7f7f7', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '10px', marginBottom: '16px' }}>
          <img src={preview.thumbnail_url} alt="" style={{ width: '90px', height: '68px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>{preview.title || '(no title)'}</div>
            {preview.channel && <div style={{ color: '#888', fontSize: '13px' }}>{preview.channel}</div>}
          </div>
        </div>
      )}
      <div style={styles.formGroup}>
        <label style={styles.label}>Title override <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
        <input
          style={styles.input}
          type="text"
          value={titleOverride}
          onChange={e => setTitleOverride(e.target.value)}
          placeholder="Leave blank to use the YouTube title"
        />
      </div>
      <div style={styles.buttonGroup}>
        <button type="submit" style={styles.button} disabled={!url.trim() || submitting}>
          {submitting ? 'Posting…' : 'Post Video Now'}
        </button>
        <button
          type="button"
          style={{ ...styles.button, ...styles.buttonSecondary }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const MessageForm = ({ onSubmit, onCancel, replyTo }) => {
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const textLength = stripHtml(content).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!stripHtml(content).trim()) {
      setError('Message content is required');
      return;
    }
    if (textLength > CHAR_HARD_LIMIT) {
      setError(`Message is too long (${textLength.toLocaleString()} characters). Please shorten it to around ${CHAR_SOFT_LIMIT.toLocaleString()} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      const success = await onSubmit({
        content,
        parent_message_id: replyTo?.message_id || null
      });
      if (success) {
        setContent('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <h4 style={{ marginBottom: '16px' }}>
        {replyTo ? `Reply to ${replyTo.display_name || 'message'}` : 'Post a Message'}
      </h4>
      {error && <div style={{ color: '#fc8181', marginBottom: '12px' }}>{error}</div>}
      <div style={styles.formGroup}>
        <RichTextEditor
          value={content}
          onChange={setContent}
          placeholder="Write your message..."
        />
        <CharacterCounter length={textLength} />
      </div>
      
      <div style={styles.buttonGroup}>
        <button type="submit" style={{
          ...styles.button,
          ...(submitting ? { opacity: 0.6, cursor: 'not-allowed' } : {})
        }} disabled={submitting || textLength > CHAR_HARD_LIMIT}>
          {submitting ? 'Posting…' : (replyTo ? 'Post Reply' : 'Post Message')}
        </button>
        {replyTo && (
          <button 
            type="button" 
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={onCancel}
          >
            Cancel Reply
          </button>
        )}
      </div>
    </form>
  );
};

// Inline message thread rendered inside the topic card
const InlineTopicThread = ({ topic, user, isAdmin, onUpgrade }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [compactMode, setCompactMode] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [watchedMessageIds, setWatchedMessageIds] = useState(new Set());
  const [watchTogglingMsgIds, setWatchTogglingMsgIds] = useState(new Set());
  const [messageReactions, setMessageReactions] = useState({});
  const [userMessageReactions, setUserMessageReactions] = useState({});

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/discussion-messages?topic_id=${topic.topic_id}`);
      setMessages(res.data);
      if (res.data.length > 0) {
        const ids = res.data.map(m => m.message_id).join(',');
        // Watch status
        api.get(`/subscriptions/check-bulk?entity_type=discussion_message&entity_ids=${ids}`)
          .then(r => {
            setWatchedMessageIds(new Set(Object.keys(r.data.subscribed).map(Number)));
          })
          .catch(() => {});
        // Message reactions
        api.get(`/discussion-reactions/counts?entity_type=message&entity_ids=${ids}`)
          .then(r => {
            setMessageReactions(r.data.counts || {});
            setUserMessageReactions(r.data.user_reactions || {});
          })
          .catch(() => {});
      } else {
        setWatchedMessageIds(new Set());
        setMessageReactions({});
        setUserMessageReactions({});
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [topic.topic_id]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handlePost = async (data) => {
    try {
      const formData = new FormData();
      formData.append('topic_id', topic.topic_id);
      formData.append('content', data.content);
      if (data.parent_message_id) {
        formData.append('parent_message_id', data.parent_message_id);
      }
      if (data.image) {
        formData.append('image', data.image);
      }
      await api.post('/discussion-messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReplyTo(null);
      setShowCompose(false);
      fetchMessages();
      // Refresh message reactions too
      return true;
    } catch (err) {
      if (err.response?.data?.upgrade_required) {
        onUpgrade(err.response.data.error);
      } else {
        console.error(err);
      }
      return false;
    }
  };

  const handleFlag = async (messageId) => {
    try {
      await api.post(`/discussion-messages/${messageId}/flag`);
      fetchMessages();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditMessage = async (messageId, content) => {
    try {
      const res = await api.put(`/discussion-messages/${messageId}`, { message_content: content });
      setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, ...res.data } : m));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleMessageWatch = async (messageId) => {
    if (!user || watchTogglingMsgIds.has(messageId)) return;
    setWatchTogglingMsgIds(prev => new Set(prev).add(messageId));
    const isWatching = watchedMessageIds.has(messageId);
    try {
      if (isWatching) {
        await api.delete('/subscriptions', { data: { entity_type: 'discussion_message', entity_id: messageId } });
        setWatchedMessageIds(prev => { const next = new Set(prev); next.delete(messageId); return next; });
      } else {
        await api.post('/subscriptions', { entity_type: 'discussion_message', entity_id: messageId });
        setWatchedMessageIds(prev => new Set(prev).add(messageId));
      }
    } catch (err) {
      console.error(err);
    }
    setWatchTogglingMsgIds(prev => { const next = new Set(prev); next.delete(messageId); return next; });
  };

  const toggleMessageReaction = async (messageId, type) => {
    if (!user) return;
    try {
      const res = await api.post('/discussion-reactions', { entity_type: 'message', entity_id: messageId, reaction_type: type });
      setMessageReactions(prev => ({ ...prev, [messageId]: res.data.counts }));
      setUserMessageReactions(prev => ({ ...prev, [messageId]: res.data.user_reaction }));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={{
      borderLeft: '4px solid #ed8936',
      backgroundColor: 'rgba(237, 137, 54, 0.04)',
      borderRadius: '0 0 8px 8px',
      padding: '16px',
    }}>
      {loading ? (
        <div style={{ color: '#718096', padding: '8px 0', fontSize: '14px' }}>Loading messages…</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <button
              onClick={() => setCompactMode(c => !c)}
              title={compactMode ? 'Expanded view' : 'Compact view'}
              style={{
                background: compactMode ? '#2563eb' : '#e2e8f0',
                color: compactMode ? 'white' : '#4a5568',
                border: 'none',
                borderRadius: '6px',
                padding: '4px 10px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              ☰ {compactMode ? 'Compact' : 'Compact'}
            </button>
          </div>
          <MessageList
            messages={messages}
            topicId={topic.topic_id}
            onReply={setReplyTo}
            onFlag={handleFlag}
            onEdit={handleEditMessage}
            currentUserId={user?.user_id}
            isAdmin={isAdmin}
            canInteract={!!user}
            replyTo={replyTo}
            onSubmitReply={handlePost}
            onCancelReply={() => setReplyTo(null)}
            compactMode={compactMode}
            watchedMessageIds={watchedMessageIds}
            watchTogglingMsgIds={watchTogglingMsgIds}
            onToggleMessageWatch={toggleMessageWatch}
            messageReactions={messageReactions}
            userMessageReactions={userMessageReactions}
            onReactToMessage={user ? toggleMessageReaction : null}
          />
          {user && !replyTo && (
            showCompose ? (
              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                  <button
                    onClick={() => setShowCompose(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#718096',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    ✕ Cancel
                  </button>
                </div>
                <MessageForm
                  onSubmit={handlePost}
                  onCancel={() => setShowCompose(false)}
                  replyTo={null}
                />
              </div>
            ) : (
              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                <button
                  onClick={() => setShowCompose(true)}
                  style={{
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  ✏️ Post Message
                </button>
              </div>
            )
          )}
          {!user && (
            <div style={{ marginTop: '12px', textAlign: 'center', color: '#718096', fontSize: '14px' }}>
              <a href="/login" style={{ color: '#2563eb', fontWeight: 600 }}>Log in</a> to join the discussion.
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TopicCard = ({ topic, isExpanded, onToggle, user, isAdmin, onUpgrade }) => {
  const [topicReactions, setTopicReactions] = useState({ up: 0, down: 0 });
  const [userTopicReaction, setUserTopicReaction] = useState(null);

  useEffect(() => {
    api.get(`/discussion-reactions/counts?entity_type=topic&entity_ids=${topic.topic_id}`)
      .then(r => {
        setTopicReactions((r.data.counts || {})[topic.topic_id] || { up: 0, down: 0 });
        setUserTopicReaction((r.data.user_reactions || {})[topic.topic_id] || null);
      })
      .catch(() => {});
  }, [topic.topic_id]);

  const toggleReaction = async (type) => {
    if (!user) return;
    try {
      const res = await api.post('/discussion-reactions', {
        entity_type: 'topic',
        entity_id: topic.topic_id,
        reaction_type: type
      });
      setTopicReactions(res.data.counts);
      setUserTopicReaction(res.data.user_reaction);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={{ marginBottom: isExpanded ? 0 : undefined }}>
      <div
        style={{
          ...styles.card,
          ...styles.topicCard,
          ...(isExpanded ? { borderRadius: '8px 8px 0 0', marginBottom: 0 } : {}),
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <div style={styles.cardTitle}>{topic.title}</div>
        {topic.youtube_url && <VideoTopicCard topic={topic} />}
        {topic.amplifier_id && <AmpTopicCard topic={topic} />}
        {/* Card footer: meta + reactions + view comments */}
        <div style={{
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
          paddingTop: '10px',
          borderTop: '1px solid #edf2f7',
        }}
          onClick={e => e.stopPropagation()}
        >
          <div style={styles.cardMeta}>
            <span>👤 {topic.creator_display_name || 'Unknown'}</span>
            <span>📅 {new Date(topic.created_at).toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {['up', 'down'].map(type => (
              <ReactionButton
                key={type}
                type={type}
                count={topicReactions[type] || 0}
                active={userTopicReaction === type}
                onClick={(e) => { e.stopPropagation(); toggleReaction(type); }}
                disabled={!user}
                entityType="topic"
                entityId={topic.topic_id}
              />
            ))}
            <CopyLinkButton url={`/discussions/topic/${topic.topic_id}`} label="🔗 Copy link" />
            <button
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              style={{
                background: isExpanded ? '#2563eb' : '#e2e8f0',
                color: isExpanded ? 'white' : '#4a5568',
                border: 'none',
                borderRadius: '8px',
                padding: '4px 14px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              💬 {isExpanded ? 'Hide' : 'View'} Comments ({topic.message_count || 0})
            </button>
          </div>
        </div>
      </div>
      {isExpanded && (
        <InlineTopicThread
          topic={topic}
          user={user}
          isAdmin={isAdmin}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  );
};

const TopicList = ({ topics, onSelectTopic, onCreateTopic, canCreate, user, isAdmin, onUpgrade }) => {
  const [expandedTopicId, setExpandedTopicId] = useState(null);

  return (
    <div>
      {canCreate && (
        <button style={styles.button} onClick={onCreateTopic}>
          + New Topic
        </button>
      )}
      {topics.length === 0 ? (
        <div style={styles.emptyState}>
          <h3>No Topics</h3>
          <p>{canCreate ? 'Start the conversation by creating a new topic!' : 'No topics yet. Log in to start a discussion.'}</p>
        </div>
      ) : (
        <div style={{ marginTop: '20px' }}>
          {topics.map(topic => (
            <TopicCard
              key={topic.topic_id}
              topic={topic}
              isExpanded={expandedTopicId === topic.topic_id}
              onToggle={() => setExpandedTopicId(prev => prev === topic.topic_id ? null : topic.topic_id)}
              user={user}
              isAdmin={isAdmin}
              onUpgrade={onUpgrade}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
const DiscussionsScreen = () => {
  const { user, isAdmin } = useAuth();
  const { topicId } = useParams(); // For direct topic URLs
  const location = useLocation();
  const [view, setView] = useState('categories'); // categories, groups, topics, messages
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [topics, setTopics] = useState([]);
  const [messages, setMessages] = useState([]);
  const [, setSelectedCategory] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [directLinkLoaded, setDirectLinkLoaded] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [postingMessage, setPostingMessage] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [watchingGroup, setWatchingGroup] = useState(false);
  const [watchingTopic, setWatchingTopic] = useState(false);
  const [watchToggling, setWatchToggling] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [watchedMessageIds, setWatchedMessageIds] = useState(new Set());
  const [watchTogglingMsgIds, setWatchTogglingMsgIds] = useState(new Set());
  const [messageReactions, setMessageReactions] = useState({});
  const [userMessageReactions, setUserMessageReactions] = useState({});

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch categories and all groups together for the unified view
      const [catRes, groupRes] = await Promise.all([
        api.get('/discussion-categories'),
        api.get('/discussion-groups')
      ]);
      setCategories(catRes.data);
      setGroups(groupRes.data);
    } catch (err) {
      setError('Failed to load discussions');
      console.error(err);
    }
    setLoading(false);
  }, []);

  const fetchTopics = useCallback(async (groupId) => {
    setLoading(true);
    try {
      const res = await api.get(`/discussion-topics?group_id=${groupId}`);
      setTopics(res.data);
    } catch (err) {
      setError('Failed to load topics');
      console.error(err);
    }
    setLoading(false);
  }, []);

  const fetchMessages = useCallback(async (topicId) => {
    setLoading(true);
    try {
      const res = await api.get(`/discussion-messages?topic_id=${topicId}`);
      setMessages(res.data);
      // Bulk-check which messages the current user is watching
      if (res.data.length > 0) {
        const ids = res.data.map(m => m.message_id).join(',');
        api.get(`/subscriptions/check-bulk?entity_type=discussion_message&entity_ids=${ids}`)
          .then(r => {
            setWatchedMessageIds(new Set(Object.keys(r.data.subscribed).map(Number)));
          })
          .catch(() => {}); // non-critical — silently ignore if unauthenticated
        api.get(`/discussion-reactions/counts?entity_type=message&entity_ids=${ids}`)
          .then(r => {
            setMessageReactions(r.data.counts || {});
            setUserMessageReactions(r.data.user_reactions || {});
          })
          .catch(() => {});
      } else {
        setWatchedMessageIds(new Set());
        setMessageReactions({});
        setUserMessageReactions({});
      }
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Handle direct topic URL - load the topic directly when accessed via /discussions/topic/:topicId
  useEffect(() => {
    const loadTopicDirectly = async () => {
      if (topicId && !directLinkLoaded && categories.length > 0 && groups.length > 0) {
        setLoading(true);
        try {
          // Fetch the topic details
          const topicRes = await api.get(`/discussion-topics/${topicId}`);
          const topic = topicRes.data;
          
          if (topic) {
            // Find the group and category for this topic
            const group = groups.find(g => g.group_id === topic.group_id);
            const category = categories.find(c => c.category_id === group?.category_id);
            
            if (group && category) {
              setSelectedCategory(category);
              setSelectedGroup(group);
              setSelectedTopic(topic);
              setView('messages');
              
              // Fetch messages for this topic
              const messagesRes = await api.get(`/discussion-messages?topic_id=${topicId}`);
              setMessages(messagesRes.data);
              // Bulk-check which messages the current user is watching (direct-link path)
              if (messagesRes.data.length > 0) {
                const ids = messagesRes.data.map(m => m.message_id).join(',');
                api.get(`/subscriptions/check-bulk?entity_type=discussion_message&entity_ids=${ids}`)
                  .then(r => {
                    setWatchedMessageIds(new Set(Object.keys(r.data.subscribed).map(Number)));
                  })
                  .catch(() => {});
              }
            }
          }
          setDirectLinkLoaded(true);
        } catch (err) {
          console.error('Failed to load topic directly:', err);
          setError('Failed to load topic');
          setDirectLinkLoaded(true);
        }
        setLoading(false);
      }
    };
    
    loadTopicDirectly();
  }, [topicId, categories, groups, directLinkLoaded]);

  // Scroll to a specific message when navigating via notification link with #message-{id}
  useEffect(() => {
    if (view === 'messages' && messages.length > 0 && location.hash) {
      const elementId = location.hash.replace('#', '');
      // Small delay to let the DOM render
      const timer = setTimeout(() => {
        const el = document.getElementById(elementId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Brief highlight to draw attention
          el.style.transition = 'background 0.3s ease';
          el.style.background = 'rgba(37, 99, 235, 0.08)';
          setTimeout(() => { el.style.background = ''; }, 2500);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [view, messages, location.hash]);

  const handleSelectCategory = (category) => {
    // Categories are now expandable in the list, no separate view needed
    setSelectedCategory(category);
  };

  const handleSelectGroup = (group) => {
    // Find the category for this group
    const category = categories.find(c => c.category_id === group.category_id);
    setSelectedCategory(category);
    setSelectedGroup(group);
    setView('topics');
    fetchTopics(group.group_id);
  };

  const handleSelectTopic = (topic) => {
    setSelectedTopic(topic);
    setView('messages');
    setShowCompose(false);
    fetchMessages(topic.topic_id);
  };

  const handleBack = () => {
    if (view === 'messages') {
      setView('topics');
      setSelectedTopic(null);
      setMessages([]);
      setReplyTo(null);
      setShowCompose(false);
    } else if (view === 'topics') {
      setView('categories');
      setSelectedGroup(null);
      setSelectedCategory(null);
      setTopics([]);
    }
  };

  const handleCreateTopic = async (data) => {
    try {
      await api.post('/discussion-topics', {
        ...data,
        group_id: selectedGroup.group_id
      });
      setShowTopicForm(false);
      fetchTopics(selectedGroup.group_id);
    } catch (err) {
      if (err.response?.data?.upgrade_required) {
        setUpgradeMessage(err.response.data.error);
        setShowUpgradeModal(true);
      } else {
        setError('Failed to create topic');
      }
      console.error(err);
    }
  };

  const handlePostVideoNow = () => {
    setShowTopicForm(false);
    fetchTopics(selectedGroup.group_id);
  };

  const handlePostMessage = async (data) => {
    try {
      if (data.image) setPostingMessage(true);
      // Use FormData to support file uploads
      const formData = new FormData();
      formData.append('topic_id', selectedTopic.topic_id);
      formData.append('content', data.content);
      if (data.parent_message_id) {
        formData.append('parent_message_id', data.parent_message_id);
      }
      if (data.image) {
        formData.append('image', data.image);
      }
      
      await api.post('/discussion-messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReplyTo(null);
      setShowCompose(false);
      fetchMessages(selectedTopic.topic_id);
      return true;
    } catch (err) {
      if (err.response?.data?.upgrade_required) {
        setUpgradeMessage(err.response.data.error);
        setShowUpgradeModal(true);
      } else if (err.response?.data?.code === 'TEXT_MODERATION_BLOCKED') {
        setError(err.response.data.error);
      } else {
        setError('Failed to post message');
      }
      console.error(err);
      return false;
    } finally {
      setPostingMessage(false);
    }
  };

  const handleFlagMessage = async (messageId) => {
    try {
      await api.post(`/discussion-messages/${messageId}/flag`);
      fetchMessages(selectedTopic.topic_id);
    } catch (err) {
      setError('Failed to flag message');
      console.error(err);
    }
  };

  const handleEditMessage = async (messageId, content) => {
    try {
      const res = await api.put(`/discussion-messages/${messageId}`, { message_content: content });
      setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, ...res.data } : m));
    } catch (err) {
      setError('Failed to edit message');
      console.error(err);
    }
  };

  // Check watch status when group or topic changes
  useEffect(() => {
    const checkWatchStatus = async () => {
      if (!user) return;
      if (view === 'topics' && selectedGroup) {
        try {
          const res = await api.get(`/subscriptions/check?entity_type=discussion_group&entity_id=${selectedGroup.group_id}`);
          setWatchingGroup(res.data.subscribed);
        } catch (err) {
          console.error('Error checking group watch status:', err);
        }
      }
      if (view === 'messages' && selectedTopic) {
        try {
          const res = await api.get(`/subscriptions/check?entity_type=discussion_topic&entity_id=${selectedTopic.topic_id}`);
          setWatchingTopic(res.data.subscribed);
        } catch (err) {
          console.error('Error checking topic watch status:', err);
        }
      }
    };
    checkWatchStatus();
  }, [user, view, selectedGroup, selectedTopic]);

  const toggleWatch = async (entityType, entityId, isWatching, setWatching) => {
    if (!user || watchToggling) return;
    setWatchToggling(true);
    try {
      if (isWatching) {
        await api.delete('/subscriptions', { data: { entity_type: entityType, entity_id: entityId } });
      } else {
        await api.post('/subscriptions', { entity_type: entityType, entity_id: entityId });
      }
      setWatching(!isWatching);
    } catch (err) {
      console.error('Error toggling watch:', err);
      setError('Failed to update watch status');
    }
    setWatchToggling(false);
  };

  const toggleMessageWatch = async (messageId) => {
    if (!user || watchTogglingMsgIds.has(messageId)) return;
    setWatchTogglingMsgIds(prev => new Set(prev).add(messageId));
    const isWatching = watchedMessageIds.has(messageId);
    try {
      if (isWatching) {
        await api.delete('/subscriptions', { data: { entity_type: 'discussion_message', entity_id: messageId } });
        setWatchedMessageIds(prev => { const next = new Set(prev); next.delete(messageId); return next; });
      } else {
        await api.post('/subscriptions', { entity_type: 'discussion_message', entity_id: messageId });
        setWatchedMessageIds(prev => new Set(prev).add(messageId));
      }
    } catch (err) {
      console.error('Error toggling message watch:', err);
      setError('Failed to update watch status');
    }
    setWatchTogglingMsgIds(prev => { const next = new Set(prev); next.delete(messageId); return next; });
  };

  const handleReactToMessage = async (messageId, type) => {
    if (!user) return;
    try {
      const res = await api.post('/discussion-reactions', { entity_type: 'message', entity_id: messageId, reaction_type: type });
      setMessageReactions(prev => ({ ...prev, [messageId]: res.data.counts }));
      setUserMessageReactions(prev => ({ ...prev, [messageId]: res.data.user_reaction }));
    } catch (err) {
      console.error('Error toggling message reaction:', err);
    }
  };

  const renderBreadcrumb = () => {
    const items = [
      { label: 'Discussions', onClick: () => { setView('categories'); setSelectedCategory(null); setSelectedGroup(null); setSelectedTopic(null); fetchCategories(); } }
    ];
    
    if (selectedGroup) {
      items.push({ 
        label: selectedGroup.group_name, 
        onClick: view === 'messages' ? () => { setView('topics'); setSelectedTopic(null); setMessages([]); fetchTopics(selectedGroup.group_id); } : null 
      });
    }
    if (selectedTopic) {
      items.push({ label: selectedTopic.title });
    }

    return (
      <div style={styles.breadcrumb}>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span style={styles.breadcrumbSeparator}>/</span>}
            {item.onClick ? (
              <span style={styles.breadcrumbLink} onClick={item.onClick}>
                {item.label}
              </span>
            ) : (
              <span style={{ color: '#4a5568' }}>{item.label}</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="dsc-container" style={styles.container}>
      <UploadOverlay
        visible={postingMessage}
        message="Posting message…"
        subtext="Uploading image & scanning for content safety"
      />
      <div className="dsc-header" style={styles.header}>
        <h1>Discussions</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {view === 'categories' && <SetHomepageButton pagePath="/discussions" />}
          {view !== 'categories' && (
            <button 
              style={{ ...styles.button, ...styles.buttonSecondary }}
              onClick={handleBack}
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {renderBreadcrumb()}

      {!user && (
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px 16px', 
          background: '#ebf8ff', 
          borderRadius: '8px', 
          borderLeft: '4px solid #4299e1',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>👋</span>
          <span style={{ color: '#2b6cb0' }}>
            You're viewing discussions in read-only mode. <a href="/login" style={{ color: '#2b6cb0', fontWeight: '600' }}>Log in</a> or <a href="/login" style={{ color: '#2b6cb0', fontWeight: '600' }}>create an account</a> to join the conversation!
          </span>
        </div>
      )}

      {error && (
        <div style={{ color: '#fc8181', marginBottom: '16px', padding: '12px', background: '#fff5f5', borderRadius: '8px' }}>
          {error}
          <button 
            style={{ marginLeft: '12px', background: 'none', border: 'none', color: '#fc8181', cursor: 'pointer' }}
            onClick={() => setError('')}
          >
            ✕
          </button>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
          Loading...
        </div>
      )}

      {!loading && view === 'categories' && (
        <CategoryList 
          categories={categories}
          groups={groups}
          onSelectCategory={handleSelectCategory}
          onSelectGroup={handleSelectGroup}
        />
      )}

      {!loading && view === 'topics' && (
        <>
          <div className="dsc-group-banner" style={{ 
            marginBottom: '20px', 
            padding: '16px 20px', 
            background: '#f7fafc', 
            borderRadius: '12px', 
            borderLeft: '4px solid #48bb78',
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
          }}>
            {/* Group Image - preserve aspect ratio */}
            {selectedGroup?.has_image ? (
              <img 
                src={`${API_URL}/discussion-groups/${selectedGroup.group_id}/image`}
                alt={selectedGroup.group_name}
                style={{ 
                  maxHeight: '100px', 
                  maxWidth: '200px',
                  borderRadius: '8px',
                  objectFit: 'contain',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
            ) : (
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '8px',
                background: '#28a745',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '40px',
                flexShrink: 0
              }}>
                💬
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ color: '#718096', fontSize: '13px', marginBottom: '4px' }}>Discussion Group</div>
              <div style={{ fontWeight: '700', color: '#2d3748', fontSize: '1.25rem' }}>{selectedGroup?.group_name}</div>
              {selectedGroup?.group_description && (
                <div style={{ color: '#718096', fontSize: '14px', marginTop: '4px' }}>{selectedGroup?.group_description}</div>
              )}
            </div>
            {user && (
              <button
                onClick={() => toggleWatch('discussion_group', selectedGroup.group_id, watchingGroup, setWatchingGroup)}
                disabled={watchToggling}
                title={watchingGroup ? 'Stop watching this group' : 'Watch this group to get notified of new topics'}
                style={{
                  background: watchingGroup ? '#48bb78' : '#e2e8f0',
                  color: watchingGroup ? 'white' : '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: watchToggling ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'background 0.2s, color 0.2s',
                  flexShrink: 0,
                  opacity: watchToggling ? 0.6 : 1
                }}
              >
                {watchingGroup ? '👁 Watching' : '👁 Watch'}
              </button>
            )}
          </div>
          {showTopicForm ? (
            selectedGroup?.topic_privilege === 'amp_videos' ? (
              <VideoTopicForm
                onSuccess={handlePostVideoNow}
                onCancel={() => setShowTopicForm(false)}
              />
            ) : (
              <TopicForm 
                onSubmit={handleCreateTopic}
                onCancel={() => setShowTopicForm(false)}
              />
            )
          ) : (
            <TopicList 
              topics={topics}
              onSelectTopic={handleSelectTopic}
              onCreateTopic={() => {
                if (user) {
                  setShowTopicForm(true);
                }
              }}
              canCreate={!!user && (
                !selectedGroup?.topic_privilege ||
                user?.is_admin
              )}
              user={user}
              isAdmin={isAdmin}
              onUpgrade={(msg) => { setUpgradeMessage(msg); setShowUpgradeModal(true); }}
            />
          )}
        </>
      )}

      {!loading && view === 'messages' && (
        <>
          <div className="dsc-topic-header" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '10px' 
          }}>
            <h2 style={{ margin: 0, color: '#2d3748' }}>{selectedTopic?.title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setCompactMode(!compactMode)}
                title={compactMode ? 'Expanded view' : 'Compact view'}
                style={{
                  background: compactMode ? '#2563eb' : '#e2e8f0',
                  color: compactMode ? 'white' : '#4a5568',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  transition: 'background 0.2s, color 0.2s'
                }}
              >
                {compactMode ? '☰' : '☰'} {compactMode ? 'Compact' : 'Compact'}
              </button>
              <ShareButton 
                url={`/discussions/topic/${selectedTopic?.topic_id}`}
                title={`Discussion: ${selectedTopic?.title}`}
                size="small"
              />
              {user && (
                <button
                  onClick={() => toggleWatch('discussion_topic', selectedTopic.topic_id, watchingTopic, setWatchingTopic)}
                  disabled={watchToggling}
                  title={watchingTopic ? 'Stop watching this topic' : 'Watch this topic to get notified of new replies'}
                  style={{
                    background: watchingTopic ? '#48bb78' : '#e2e8f0',
                    color: watchingTopic ? 'white' : '#4a5568',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: watchToggling ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    transition: 'background 0.2s, color 0.2s',
                    opacity: watchToggling ? 0.6 : 1
                  }}
                >
                  {watchingTopic ? '👁 Watching' : '👁 Watch'}
                </button>
              )}
            </div>
          </div>
          
          {/* Video card if this topic is a YouTube video */}
          {selectedTopic?.youtube_url && (
            <div style={{ marginBottom: '20px' }}>
              <VideoTopicCard topic={selectedTopic} />
            </div>
          )}

          {/* Link to amplifier if this topic is about an amp */}
          {selectedTopic?.amplifier_id && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '12px 16px', 
              background: 'rgba(37, 99, 235, 0.06)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              
              <span style={{ color: '#4a5568', fontWeight: '500' }}>
                {selectedTopic.amplifier_manufacturer} {selectedTopic.amplifier_name}
              </span>
              <button
                onClick={() => window.open(selectedTopic.manufacturer_slug && selectedTopic.amplifier_slug ? `/amps/${selectedTopic.manufacturer_slug}/${selectedTopic.amplifier_slug}` : `/amplifier-components/${selectedTopic.amplifier_id}`, '_blank')}
                title="Clicking this button takes you to the amplifier this discussion thread refers to"
                style={{
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.4)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(37, 99, 235, 0.4)';
                }}
              >
                <span>→</span> Jump to Amplifier
              </button>
            </div>
          )}
          
          {/* Top-level compose form — hidden by default, opened via button */}
          {user && !replyTo && (
            showCompose ? (
              <MessageForm 
                onSubmit={handlePostMessage}
                onCancel={() => setShowCompose(false)}
                replyTo={null}
              />
            ) : (
              <div style={{ marginBottom: '16px' }}>
                <button
                  onClick={() => setShowCompose(true)}
                  style={{
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
                  }}
                >
                  + Add a Reply
                </button>
              </div>
            )
          )}
          
          <MessageList 
            messages={messages}
            topicId={selectedTopic?.topic_id}
            onReply={setReplyTo}
            onFlag={handleFlagMessage}
            onEdit={handleEditMessage}
            currentUserId={user?.user_id}
            isAdmin={isAdmin}
            canInteract={!!user}
            replyTo={replyTo}
            onSubmitReply={handlePostMessage}
            onCancelReply={() => setReplyTo(null)}
            compactMode={compactMode}
            watchedMessageIds={watchedMessageIds}
            watchTogglingMsgIds={watchTogglingMsgIds}
            onToggleMessageWatch={toggleMessageWatch}
            messageReactions={messageReactions}
            userMessageReactions={userMessageReactions}
            onReactToMessage={user ? handleReactToMessage : null}
          />
        </>
      )}

      {/* Upgrade to Pro Modal */}
      <UpgradeModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        message={upgradeMessage}
      />
    </div>
  );
};

export { VideoTopicCard, AmpTopicCard, InlineTopicThread, CollapsibleMessage };
export default DiscussionsScreen;
